import React, { useState, useEffect } from 'react';
import {
  Shield, Users, FileText, Lock, AlertTriangle, CheckCircle,
  Eye, Download, RefreshCw, Settings, UserCog, Key, Activity,
  Smartphone, Monitor, X, Copy, LogOut
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { useToast } from '../../hooks/use-toast';

const SecurityTab = () => {
  const [activeSection, setActiveSection] = useState('roles');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  
  // Roles states
  const [rolePolicies, setRolePolicies] = useState({});
  const [adminUsers, setAdminUsers] = useState([]);
  const [selectedAdminForRole, setSelectedAdminForRole] = useState(null);
  const [newRole, setNewRole] = useState('');
  
  // Audit states
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditFilters, setAuditFilters] = useState({
    admin_id: '',
    target_type: '',
    action: '',
    start_date: '',
    end_date: '',
    limit: 50,
    skip: 0
  });
  const [auditTotal, setAuditTotal] = useState(0);
  const [chainVerification, setChainVerification] = useState(null);
  
  // Security settings states
  const [securitySettings, setSecuritySettings] = useState({});
  
  // 2FA states
  const [twoFAStatus, setTwoFAStatus] = useState({ enrolled: false, enabled: false });
  const [twoFAEnrollment, setTwoFAEnrollment] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFAAdoption, setTwoFAAdoption] = useState({ total_admins: 0, enabled_count: 0, adoption_percentage: 0 });
  
  // Sessions states
  const [mySessions, setMySessions] = useState([]);
  const [allSessions, setAllSessions] = useState([]);
  const [sessionStats, setSessionStats] = useState({});
  
  const API_BASE_URL = '/api';
  const adminToken = localStorage.getItem('auth_token');
  
  useEffect(() => {
    if (activeSection === 'roles') {
      fetchRolePolicies();
      fetchAdminUsers();
    } else if (activeSection === 'audit') {
      fetchAuditLogs();
    } else if (activeSection === 'settings') {
      fetchSecuritySettings();
    } else if (activeSection === 'staff') {
      fetch2FAStatus();
      fetch2FAAdoption();
      fetchMySessions();
      fetchAllSessions();
      fetchSessionStats();
    }
  }, [activeSection]);
  
  // ==================== ROLES ====================
  
  const fetchRolePolicies = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/security/roles`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setRolePolicies(data.policies);
      }
    } catch (error) {
      console.error('Error fetching role policies:', error);
    }
  };
  
  const fetchAdminUsers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/security/admins`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setAdminUsers(data.admins);
      }
    } catch (error) {
      console.error('Error fetching admin users:', error);
    }
  };
  
  const assignRole = async () => {
    if (!selectedAdminForRole || !newRole) {
      toast({
        title: "Missing Information",
        description: "Please select an admin and a role",
        variant: "destructive"
      });
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/security/admins/${selectedAdminForRole.id}/role`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ role: newRole })
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Role Updated",
          description: `Successfully assigned ${newRole} role`,
        });
        
        setSelectedAdminForRole(null);
        setNewRole('');
        fetchAdminUsers();
      } else {
        throw new Error(data.detail || 'Failed to assign role');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  // ==================== AUDIT LOGS ====================
  
  const fetchAuditLogs = async () => {
    setLoading(true);
    
    try {
      const params = new URLSearchParams();
      Object.keys(auditFilters).forEach(key => {
        if (auditFilters[key]) {
          params.append(key, auditFilters[key]);
        }
      });
      
      const response = await fetch(
        `${API_BASE_URL}/security/audit/logs?${params.toString()}`,
        {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        setAuditLogs(data.logs);
        setAuditTotal(data.total);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast({
        title: "Error",
        description: "Failed to fetch audit logs",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const verifyAuditChain = async () => {
    setLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/security/audit/verify`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setChainVerification(data);
        
        if (data.verified) {
          toast({
            title: "Chain Verified ✓",
            description: `Checked ${data.checked_count} records - no tampering detected`,
          });
        } else {
          toast({
            title: "Tampering Detected!",
            description: `Found ${data.tampered_records?.length || 0} tampered records`,
            variant: "destructive"
          });
        }
      }
    } catch (error) {
      toast({
        title: "Verification Failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const exportAuditLogs = async () => {
    try {
      const params = new URLSearchParams();
      Object.keys(auditFilters).forEach(key => {
        if (auditFilters[key] && key !== 'limit' && key !== 'skip') {
          params.append(key, auditFilters[key]);
        }
      });
      
      const response = await fetch(
        `${API_BASE_URL}/security/audit/export?${params.toString()}`,
        {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit_logs_${new Date().toISOString()}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        
        toast({
          title: "Export Complete",
          description: "Audit logs downloaded successfully",
        });
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      toast({
        title: "Export Failed",
        description: error.message,
        variant: "destructive"
      });
    }
  };
  
  // ==================== SECURITY SETTINGS ====================
  
  const fetchSecuritySettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/security/settings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setSecuritySettings(data.settings);
      }
    } catch (error) {
      console.error('Error fetching security settings:', error);
    }
  };
  
  const updateSecuritySettings = async (updates) => {
    setLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/security/settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Settings Updated",
          description: "Security settings saved successfully",
        });
        fetchSecuritySettings();
      } else {
        throw new Error(data.detail || 'Update failed');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  // ==================== 2FA ====================
  
  const fetch2FAStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/security/2fa/status`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setTwoFAStatus(data);
      }
    } catch (error) {
      console.error('Error fetching 2FA status:', error);
    }
  };
  
  const fetch2FAAdoption = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/security/2fa/adoption`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setTwoFAAdoption(data);
      }
    } catch (error) {
      console.error('Error fetching 2FA adoption:', error);
    }
  };
  
  const enroll2FA = async () => {
    setLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/security/2fa/enroll`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTwoFAEnrollment(data);
        toast({
          title: "2FA Enrollment Started",
          description: "Scan the QR code with your authenticator app",
        });
      } else {
        throw new Error(data.error || 'Enrollment failed');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const verify2FA = async () => {
    if (!twoFACode || twoFACode.length !== 6) {
      toast({
        title: "Invalid Code",
        description: "Please enter a 6-digit code",
        variant: "destructive"
      });
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/security/2fa/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: twoFACode })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "2FA Enabled!",
          description: "Two-factor authentication is now active",
        });
        setTwoFAEnrollment(null);
        setTwoFACode('');
        fetch2FAStatus();
        fetch2FAAdoption();
      } else {
        toast({
          title: "Verification Failed",
          description: data.error || "Invalid code",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const disable2FA = async () => {
    if (!confirm('Are you sure you want to disable 2FA? This will make your account less secure.')) return;
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/security/2fa/disable`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "2FA Disabled",
          description: "Two-factor authentication has been disabled",
        });
        fetch2FAStatus();
        fetch2FAAdoption();
      } else {
        throw new Error(data.error || 'Failed to disable');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const copyBackupCode = (code) => {
    navigator.clipboard.writeText(code);
    toast({
      title: "Copied",
      description: "Backup code copied to clipboard",
    });
  };
  
  // ==================== SESSIONS ====================
  
  const fetchMySessions = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/security/sessions/my`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setMySessions(data.sessions);
      }
    } catch (error) {
      console.error('Error fetching my sessions:', error);
    }
  };
  
  const fetchAllSessions = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/security/sessions/all`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setAllSessions(data.sessions);
      }
    } catch (error) {
      console.error('Error fetching all sessions:', error);
    }
  };
  
  const fetchSessionStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/security/sessions/stats`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setSessionStats(data);
      }
    } catch (error) {
      console.error('Error fetching session stats:', error);
    }
  };
  
  const revokeSession = async (sessionId) => {
    if (!confirm('Are you sure you want to revoke this session? The user will be logged out.')) return;
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/security/sessions/${sessionId}/revoke`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Session Revoked",
          description: "User has been logged out",
        });
        fetchMySessions();
        fetchAllSessions();
        fetchSessionStats();
      } else {
        throw new Error(data.error || 'Failed to revoke');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const revokeAllSessions = async (adminId) => {
    if (!confirm('Are you sure you want to log out this admin from ALL devices?')) return;
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/security/sessions/revoke-all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ admin_id: adminId })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "All Sessions Revoked",
          description: `Logged out from ${data.revoked_count} devices`,
        });
        fetchAllSessions();
        fetchSessionStats();
      } else {
        throw new Error(data.error || 'Failed to revoke');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  // ==================== RENDER ====================
  
  return (
    <div className="space-y-6" data-testid="security-tab">
      {/* Section Navigation */}
      <div className="inline-flex rounded-2xl p-1 backdrop-blur-xl"
           style={{
             background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0.5) 100%)',
             border: '1px solid rgba(255, 182, 193, 0.2)',
           }}>
        <button
          onClick={() => setActiveSection('roles')}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
            activeSection === 'roles'
              ? 'bg-gradient-to-r from-purple-400 to-purple-600 text-white shadow-lg'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          data-testid="roles-tab-btn"
        >
          <UserCog className="w-5 h-5" />
          <span>Roles & Access</span>
        </button>
        <button
          onClick={() => setActiveSection('staff')}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
            activeSection === 'staff'
              ? 'bg-gradient-to-r from-pink-400 to-pink-600 text-white shadow-lg'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          data-testid="staff-tab-btn"
        >
          <Key className="w-5 h-5" />
          <span>Staff Security</span>
        </button>
        <button
          onClick={() => setActiveSection('audit')}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
            activeSection === 'audit'
              ? 'bg-gradient-to-r from-blue-400 to-blue-600 text-white shadow-lg'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          data-testid="audit-tab-btn"
        >
          <FileText className="w-5 h-5" />
          <span>Audit Logs</span>
        </button>
        <button
          onClick={() => setActiveSection('settings')}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
            activeSection === 'settings'
              ? 'bg-gradient-to-r from-emerald-400 to-emerald-600 text-white shadow-lg'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          data-testid="settings-tab-btn"
        >
          <Settings className="w-5 h-5" />
          <span>Settings</span>
        </button>
      </div>
      
      {/* Roles & Access Section */}
      {activeSection === 'roles' && (
        <div className="space-y-6">
          {/* Role Policies Matrix */}
          <Card className="p-6" data-testid="roles-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-xl bg-gradient-to-br from-purple-400 to-purple-600">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Role Policies</h3>
                <p className="text-sm text-gray-500">Permission matrix for each role</p>
              </div>
            </div>
            
            {Object.keys(rolePolicies).length > 0 ? (
              <div className="space-y-4">
                {Object.entries(rolePolicies).map(([role, policy]) => (
                  <div key={role} className="border rounded-xl p-4 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-bold text-lg capitalize">{role.replace('_', ' ')}</h4>
                        <p className="text-sm text-gray-600">{policy.description}</p>
                      </div>
                      <div className="flex gap-2">
                        {policy.can_assign_roles && (
                          <span className="px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-700">
                            Can Assign Roles
                          </span>
                        )}
                        {policy.can_manage_security && (
                          <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-700">
                            Security Manager
                          </span>
                        )}
                        {policy.can_export_data && (
                          <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">
                            Data Export
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {policy.permissions.map((perm, idx) => (
                        <span key={idx} className="px-3 py-1 rounded-lg text-xs font-mono bg-white border border-gray-200">
                          {perm}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Loading role policies...</p>
            )}
          </Card>
          
          {/* Admin Users List */}
          <Card className="p-6" data-testid="admin-users-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Admin Users</h3>
                <p className="text-sm text-gray-500">Manage roles and permissions</p>
              </div>
            </div>
            
            {adminUsers.length > 0 ? (
              <div className="space-y-3">
                {adminUsers.map((admin) => (
                  <div key={admin.id} className="flex justify-between items-center p-4 border rounded-xl hover:bg-gray-50">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{admin.email}</div>
                      <div className="text-sm text-gray-500">Username: {admin.username}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        admin.role === 'super_admin' ? 'bg-purple-100 text-purple-700' :
                        admin.role === 'admin' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {admin.role?.replace('_', ' ') || 'Support'}
                      </span>
                      <Button
                        onClick={() => {
                          setSelectedAdminForRole(admin);
                          setNewRole(admin.role || 'support');
                        }}
                        variant="outline"
                        size="sm"
                        data-testid={`change-role-btn-${admin.id}`}
                      >
                        <UserCog className="w-4 h-4 mr-1" />
                        Change Role
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No admin users found</p>
            )}
          </Card>
        </div>
      )}
      
      {/* Staff Security Section */}
      {activeSection === 'staff' && (
        <div className="space-y-6">
          {/* 2FA Adoption Card */}
          <Card className="p-6" data-testid="2fa-adoption-card">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-gradient-to-br from-pink-400 to-pink-600">
                  <Smartphone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">2FA Adoption</h3>
                  <p className="text-sm text-gray-500">Two-factor authentication status</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-pink-600">{twoFAAdoption.adoption_percentage}%</div>
                <div className="text-sm text-gray-500">{twoFAAdoption.enabled_count} of {twoFAAdoption.total_admins} admins</div>
              </div>
            </div>
          </Card>
          
          {/* My 2FA Status */}
          <Card className="p-6" data-testid="my-2fa-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600">
                <Key className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">My 2FA Settings</h3>
                <p className="text-sm text-gray-500">Manage your two-factor authentication</p>
              </div>
            </div>
            
            {twoFAStatus.enabled ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <div>
                      <div className="font-medium text-green-900">2FA Enabled</div>
                      <div className="text-sm text-green-700">Your account is protected with 2FA</div>
                    </div>
                  </div>
                  <Button
                    onClick={disable2FA}
                    variant="outline"
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    disabled={loading}
                    data-testid="disable-2fa-btn"
                  >
                    Disable 2FA
                  </Button>
                </div>
              </div>
            ) : twoFAEnrollment ? (
              <div className="space-y-4">
                <div className="text-center">
                  <img src={twoFAEnrollment.qr_code_url} alt="QR Code" className="mx-auto w-64 h-64 border rounded-xl" />
                  <p className="mt-4 text-sm text-gray-600">Scan this QR code with your authenticator app</p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="text-sm font-medium text-gray-700 mb-2">Manual Entry Code:</div>
                  <div className="font-mono text-sm bg-white p-3 rounded border">{twoFAEnrollment.secret}</div>
                </div>
                
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="font-medium text-amber-900 mb-2">Backup Codes</div>
                  <div className="text-sm text-amber-700 mb-3">Save these codes securely. Each can be used once if you lose access to your device.</div>
                  <div className="grid grid-cols-2 gap-2">
                    {twoFAEnrollment.backup_codes.map((code, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white p-2 rounded border">
                        <span className="font-mono text-sm">{code}</span>
                        <button onClick={() => copyBackupCode(code)} className="text-gray-500 hover:text-gray-700">
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Enter 6-digit code from your app:
                  </label>
                  <input
                    type="text"
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="w-full px-4 py-3 text-center text-2xl font-mono border rounded-xl tracking-widest"
                    maxLength={6}
                    data-testid="2fa-code-input"
                  />
                  <Button
                    onClick={verify2FA}
                    disabled={loading || twoFACode.length !== 6}
                    className="w-full"
                    data-testid="verify-2fa-btn"
                  >
                    {loading ? 'Verifying...' : 'Verify & Enable 2FA'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                <h4 className="font-medium text-gray-900 mb-2">2FA Not Enabled</h4>
                <p className="text-sm text-gray-600 mb-4">Protect your account with two-factor authentication</p>
                <Button
                  onClick={enroll2FA}
                  disabled={loading}
                  data-testid="enroll-2fa-btn"
                >
                  <Smartphone className="w-4 h-4 mr-2" />
                  Enable 2FA
                </Button>
              </div>
            )}
          </Card>
          
          {/* My Sessions */}
          <Card className="p-6" data-testid="my-sessions-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-xl bg-gradient-to-br from-indigo-400 to-indigo-600">
                <Monitor className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">My Active Sessions</h3>
                <p className="text-sm text-gray-500">Devices where you're currently logged in</p>
              </div>
            </div>
            
            {mySessions.length > 0 ? (
              <div className="space-y-3">
                {mySessions.map((session) => (
                  <div key={session.id} className="flex items-center justify-between p-4 border rounded-xl hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-indigo-50 rounded-lg">
                        {session.device_info?.is_mobile ? (
                          <Smartphone className="w-5 h-5 text-indigo-600" />
                        ) : (
                          <Monitor className="w-5 h-5 text-indigo-600" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {session.device_info?.browser || 'Unknown Browser'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {session.device_info?.os || 'Unknown OS'} • {session.ip}
                        </div>
                        <div className="text-xs text-gray-400">
                          Last seen: {new Date(session.last_seen).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <Button
                      onClick={() => revokeSession(session.id)}
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      data-testid={`revoke-session-btn-${session.id}`}
                    >
                      <LogOut className="w-4 h-4 mr-1" />
                      Log Out
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">No active sessions</p>
            )}
          </Card>
          
          {/* All Sessions (Super Admin only) */}
          <Card className="p-6" data-testid="all-sessions-card">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-gradient-to-br from-purple-400 to-purple-600">
                  <Activity className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">All Active Sessions</h3>
                  <p className="text-sm text-gray-500">Monitor all admin sessions</p>
                </div>
              </div>
              {sessionStats.total_active_sessions > 0 && (
                <div className="text-right">
                  <div className="text-2xl font-bold text-purple-600">{sessionStats.total_active_sessions}</div>
                  <div className="text-sm text-gray-500">{sessionStats.unique_active_admins} unique admins</div>
                </div>
              )}
            </div>
            
            {allSessions.length > 0 ? (
              <div className="space-y-3">
                {allSessions.map((session) => (
                  <div key={session.id} className="flex items-center justify-between p-4 border rounded-xl hover:bg-gray-50">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900">{session.admin_email}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          session.admin_role === 'super_admin' ? 'bg-purple-100 text-purple-700' :
                          session.admin_role === 'admin' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {session.admin_role}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {session.device_info?.browser} on {session.device_info?.os}
                      </div>
                      <div className="text-xs text-gray-400">
                        {session.ip} • Last seen: {new Date(session.last_seen).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => revokeSession(session.id)}
                        variant="outline"
                        size="sm"
                        data-testid={`revoke-session-admin-btn-${session.id}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">No active sessions</p>
            )}
          </Card>
        </div>
      )}
      
      {/* Audit Logs Section */}
      {activeSection === 'audit' && (
        <div className="space-y-6">
          <Card className="p-6" data-testid="audit-logs-card">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Audit Logs</h3>
                  <p className="text-sm text-gray-500">Tamper-evident activity trail</p>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={verifyAuditChain}
                  disabled={loading}
                  variant="outline"
                  data-testid="verify-chain-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Verify Chain
                </Button>
                <Button
                  onClick={exportAuditLogs}
                  variant="outline"
                  data-testid="export-audit-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
                <Button
                  onClick={fetchAuditLogs}
                  disabled={loading}
                  data-testid="refresh-audit-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </div>
            
            {/* Chain Verification Result */}
            {chainVerification && (
              <div className={`mb-4 p-4 rounded-xl ${
                chainVerification.verified ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center gap-2">
                  {chainVerification.verified ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  )}
                  <span className={`font-medium ${
                    chainVerification.verified ? 'text-green-900' : 'text-red-900'
                  }`}>
                    {chainVerification.verified
                      ? `Chain Verified: ${chainVerification.checked_count} records checked`
                      : `Tampering Detected: ${chainVerification.tampered_records?.length || 0} tampered records`
                    }
                  </span>
                </div>
              </div>
            )}
            
            {/* Filters */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <select
                value={auditFilters.action}
                onChange={(e) => setAuditFilters({ ...auditFilters, action: e.target.value })}
                className="px-3 py-2 border rounded-lg text-sm"
                data-testid="audit-action-filter"
              >
                <option value="">All Actions</option>
                <option value="create">Create</option>
                <option value="update">Update</option>
                <option value="delete">Delete</option>
                <option value="login">Login</option>
                <option value="export">Export</option>
              </select>
              
              <select
                value={auditFilters.target_type}
                onChange={(e) => setAuditFilters({ ...auditFilters, target_type: e.target.value })}
                className="px-3 py-2 border rounded-lg text-sm"
                data-testid="audit-target-filter"
              >
                <option value="">All Targets</option>
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="integration">Integration</option>
                <option value="security_settings">Security Settings</option>
              </select>
              
              <input
                type="date"
                value={auditFilters.start_date}
                onChange={(e) => setAuditFilters({ ...auditFilters, start_date: e.target.value })}
                className="px-3 py-2 border rounded-lg text-sm"
                data-testid="audit-start-date"
              />
              
              <input
                type="date"
                value={auditFilters.end_date}
                onChange={(e) => setAuditFilters({ ...auditFilters, end_date: e.target.value })}
                className="px-3 py-2 border rounded-lg text-sm"
                data-testid="audit-end-date"
              />
            </div>
            
            {/* Logs Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admin</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {auditLogs.length > 0 ? (
                    auditLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50" data-testid={`audit-log-${log.id}`}>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {new Date(log.timestamp).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="font-medium text-gray-900">{log.admin_email}</div>
                          <div className="text-xs text-gray-500">{log.admin_role}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            {log.action}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {log.target_type}
                          {log.target_id && <div className="text-xs text-gray-500 font-mono">{log.target_id.substring(0, 8)}...</div>}
                        </td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-500">
                          {log.ip}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                        {loading ? 'Loading...' : 'No audit logs found'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {auditTotal > 0 && (
              <div className="mt-4 text-sm text-gray-500 text-center">
                Showing {auditLogs.length} of {auditTotal} total logs
              </div>
            )}
          </Card>
        </div>
      )}
      
      {/* Security Settings Section */}
      {activeSection === 'settings' && (
        <div className="space-y-6">
          <Card className="p-6" data-testid="security-settings-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600">
                <Lock className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Security Settings</h3>
                <p className="text-sm text-gray-500">Global security configuration</p>
              </div>
            </div>
            
            <div className="space-y-6">
              {/* 2FA Setting */}
              <div className="flex items-center justify-between p-4 border rounded-xl">
                <div>
                  <h4 className="font-medium text-gray-900">Require 2FA Globally</h4>
                  <p className="text-sm text-gray-500">Force all admins to enable two-factor authentication</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={securitySettings.require_2fa_globally || false}
                    onChange={(e) => updateSecuritySettings({ require_2fa_globally: e.target.checked })}
                    className="sr-only peer"
                    data-testid="require-2fa-toggle"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>
              
              {/* Session Timeout */}
              <div className="p-4 border rounded-xl">
                <label className="block mb-2">
                  <span className="font-medium text-gray-900">Session Timeout (hours)</span>
                  <p className="text-sm text-gray-500">Automatically log out admins after inactivity</p>
                </label>
                <input
                  type="number"
                  min="1"
                  max="24"
                  value={securitySettings.session_timeout_hours || 8}
                  onChange={(e) => setSecuritySettings({
                    ...securitySettings,
                    session_timeout_hours: parseInt(e.target.value)
                  })}
                  className="w-full px-4 py-2 border rounded-lg"
                  data-testid="session-timeout-input"
                />
                <Button
                  onClick={() => updateSecuritySettings({
                    session_timeout_hours: securitySettings.session_timeout_hours
                  })}
                  className="mt-2"
                  size="sm"
                >
                  Save
                </Button>
              </div>
              
              {/* Breach Mode */}
              <div className="p-4 border-2 border-red-200 rounded-xl bg-red-50">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className="font-bold text-red-900 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Breach Mode
                    </h4>
                    <p className="text-sm text-red-700">Emergency security lockdown</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    securitySettings.breach_mode
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-200 text-gray-700'
                  }`}>
                    {securitySettings.breach_mode ? 'ACTIVE' : 'Inactive'}
                  </span>
                </div>
                <p className="text-sm text-red-700 mb-3">
                  Activating breach mode will force re-authentication, invalidate sessions, and trigger security audits.
                </p>
                <Button
                  onClick={() => {
                    if (confirm('Are you sure you want to activate breach mode? This will log out all admins.')) {
                      updateSecuritySettings({ breach_mode: !securitySettings.breach_mode });
                    }
                  }}
                  variant="outline"
                  className={securitySettings.breach_mode ? 'border-green-600 text-green-700' : 'border-red-600 text-red-700'}
                  data-testid="breach-mode-toggle"
                >
                  {securitySettings.breach_mode ? 'Deactivate' : 'Activate'} Breach Mode
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
      
      {/* Role Assignment Modal */}
      {selectedAdminForRole && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedAdminForRole(null)}>
          <Card className="w-full max-w-md m-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 space-y-4">
              <h3 className="text-xl font-bold text-gray-900">Change Admin Role</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin</label>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="font-medium">{selectedAdminForRole.email}</div>
                  <div className="text-sm text-gray-500">Current: {selectedAdminForRole.role}</div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Role</label>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  className="w-full px-4 py-2 border rounded-xl"
                  data-testid="role-select"
                >
                  <option value="super_admin">Super Admin</option>
                  <option value="admin">Admin</option>
                  <option value="support">Support</option>
                </select>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  onClick={() => setSelectedAdminForRole(null)}
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  onClick={assignRole}
                  disabled={loading}
                  data-testid="confirm-role-change-btn"
                >
                  {loading ? 'Updating...' : 'Update Role'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SecurityTab;
